import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createApiKey,
  listApiKeys,
  revokeApiKey,
  type ApiKey,
  type CreatedApiKey,
} from '../api/endpoints.js';
import { errorMessage } from '../api/client.js';
import { PageHeader } from '../components/PageHeader.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';

// The CRM is served on several domains (tipscontact.com, crm.databaseemailer.app, …),
// each with its API on `api.<apex>`. Derive the MCP endpoint from the current host so
// the key/command shown matches the domain the user is actually on — instead of always
// pointing at api.crm.databaseemailer.app.
function mcpEndpoint(): string {
  if (typeof window === 'undefined') return 'https://api.crm.databaseemailer.app/mcp';
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8080/mcp';
  const apex = hostname.replace(/^www\./, '');
  return `${protocol}//api.${apex}/mcp`;
}

const MCP_URL = mcpEndpoint();

function fmt(d: string | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleString();
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn btn--secondary btn--sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard may be unavailable */
        }
      }}
    >
      {copied ? 'Copied!' : label}
    </button>
  );
}

function CreatedKeyModal({ created, onClose }: { created: CreatedApiKey; onClose: () => void }) {
  const claudeCmd = `claude mcp add --transport http tips-crm ${MCP_URL} --header "Authorization: Bearer ${created.key}"`;
  return (
    <Modal open title={`API key created — ${created.name}`} onClose={onClose}>
      <div className="snippet-block">
        <div className="notice notice--info">
          Copy this key now — it's shown <strong>only once</strong> and cannot be retrieved later.
        </div>
        <label className="field">
          <span className="field__label">Your API key</span>
          <pre className="code-block">{created.key}</pre>
        </label>
        <div className="form__actions">
          <CopyButton text={created.key} label="Copy key" />
        </div>

        <p className="muted" style={{ marginTop: 18 }}>Connect it to Claude Code:</p>
        <pre className="code-block">{claudeCmd}</pre>
        <div className="form__actions">
          <CopyButton text={claudeCmd} label="Copy command" />
        </div>
        <p className="muted small" style={{ marginTop: 12 }}>
          MCP endpoint: <code>{MCP_URL}</code>. The key inherits your role and seller scope.
        </p>

        <div className="form__actions" style={{ marginTop: 18 }}>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function ApiKeysPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [created, setCreated] = useState<CreatedApiKey | null>(null);

  const keysQ = useQuery({ queryKey: ['api-keys'], queryFn: listApiKeys });

  const createMutation = useMutation({
    mutationFn: () => createApiKey(name.trim()),
    onSuccess: (data) => {
      setCreated(data);
      setOpen(false);
      setName('');
      void qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });

  const columns = useMemo<Column<ApiKey>[]>(
    () => [
      { key: 'name', header: 'Name', render: (k) => k.name },
      { key: 'created', header: 'Created', render: (k) => fmt(k.created_at) },
      { key: 'used', header: 'Last used', render: (k) => fmt(k.last_used_at) },
      {
        key: 'status',
        header: 'Status',
        render: (k) =>
          k.revoked_at ? (
            <span className="badge badge--status-inactive">revoked</span>
          ) : (
            <span className="badge badge--status-active">active</span>
          ),
      },
      {
        key: 'actions',
        header: '',
        render: (k) =>
          k.revoked_at ? null : (
            <div className="row-actions" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="btn btn--danger btn--sm"
                onClick={() => {
                  if (confirm(`Revoke key "${k.name}"? Anything using it will stop working immediately.`)) {
                    revokeMutation.mutate(k.id);
                  }
                }}
              >
                Revoke
              </button>
            </div>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revokeMutation],
  );

  return (
    <div className="page">
      <PageHeader
        title="API Keys"
        subtitle="Keys for the CRM's MCP server (use the CRM from Claude, Cursor, etc.) and programmatic access. Each key inherits your role and seller scope."
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>
            New key
          </button>
        }
      />

      <div className="card" style={{ marginBottom: 18 }}>
        <p className="muted" style={{ margin: 0 }}>
          MCP endpoint: <code>{MCP_URL}</code> — connect an AI assistant to search contacts, review
          calls/recordings &amp; transcripts, add notes, manage tasks, and run reports in natural
          language. Reads are auto-approved; writes ask for confirmation.
        </p>
      </div>

      {revokeMutation.isError && <div className="form-error">{errorMessage(revokeMutation.error)}</div>}

      <DataTable
        columns={columns}
        rows={keysQ.data}
        rowKey={(k) => k.id}
        isLoading={keysQ.isLoading}
        isError={keysQ.isError}
        error={keysQ.error}
        onRetry={() => keysQ.refetch()}
        emptyMessage="No API keys yet. Create one to connect an AI assistant."
      />

      <Modal open={open} title="New API key" onClose={() => setOpen(false)}>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <label className="field">
            <span className="field__label">Name</span>
            <input
              className="field__input"
              placeholder="e.g. My laptop — Claude"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </label>
          {createMutation.isError && <div className="form-error">{errorMessage(createMutation.error)}</div>}
          <div className="form__actions">
            <button type="button" className="btn btn--ghost" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={createMutation.isPending || !name.trim()}
            >
              {createMutation.isPending ? 'Creating…' : 'Create key'}
            </button>
          </div>
        </form>
      </Modal>

      {created && <CreatedKeyModal created={created} onClose={() => setCreated(null)} />}
    </div>
  );
}
